package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.ui.store.BucketKeyInput
import com.advaita.cloud.drive.ui.store.CuratedPick
import com.advaita.cloud.drive.ui.store.ExtractChoice
import com.advaita.cloud.drive.ui.store.ExtractPickerAt
import com.advaita.cloud.drive.ui.store.label
import com.advaita.cloud.drive.ui.store.ExtractToUiState
import com.advaita.cloud.drive.ui.store.CuratedWorkflowSpec
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import com.advaita.cloud.drive.ui.store.OpcRunSelection
import com.advaita.cloud.drive.ui.store.OpcWizardState
import com.advaita.cloud.drive.ui.store.OpcWizardStep
import com.advaita.cloud.drive.ui.store.PickerSource
import com.advaita.cloud.drive.ui.store.PreviewTooltipStore
import com.advaita.cloud.drive.ui.store.PullModalStep
import com.advaita.cloud.drive.ui.store.PullSubmission
import com.advaita.cloud.drive.ui.store.advance
import com.advaita.cloud.drive.ui.store.allBucketsFilled
import com.advaita.cloud.drive.ui.store.back
import com.advaita.cloud.drive.ui.store.bucketObjectCounts
import com.advaita.cloud.drive.ui.store.buildS3Credentials
import com.advaita.cloud.drive.ui.store.canAdvance
import com.advaita.cloud.drive.ui.store.canRun
import com.advaita.cloud.drive.ui.store.choose
import com.advaita.cloud.drive.ui.store.clampCredentialPage
import com.advaita.cloud.drive.ui.store.credentialDots
import com.advaita.cloud.drive.ui.store.credentialPageBucket
import com.advaita.cloud.drive.ui.store.credentialPageCount
import com.advaita.cloud.drive.ui.store.credentialPageCounter
import com.advaita.cloud.drive.ui.store.credentialPageFilled
import com.advaita.cloud.drive.ui.store.distinctS3Buckets
import com.advaita.cloud.drive.ui.store.initialOpcWizardState
import com.advaita.cloud.drive.ui.store.isLastCredentialPage
import com.advaita.cloud.drive.ui.store.withPicks
import com.advaita.cloud.drive.ui.store.needsStepFlow
import com.advaita.cloud.drive.ui.store.parsePullUrls
import com.advaita.cloud.drive.ui.store.s3PullBlocked
import kotlinx.coroutines.CompletableDeferred
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.autoFocus
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.PasswordInput
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

sealed class ModalRequest(
    val title: String,
    val message: String?,
    val confirmLabel: String,
    val cancelLabel: String,
) {
    class Confirm(
        title: String,
        message: String?,
        confirmLabel: String,
        cancelLabel: String,
        val destructive: Boolean,
        val completer: CompletableDeferred<Boolean>,
    ) : ModalRequest(title, message, confirmLabel, cancelLabel)

    class Prompt(
        title: String,
        message: String?,
        confirmLabel: String,
        cancelLabel: String,
        val inputLabel: String,
        val placeholder: String,
        val defaultValue: String,
        val completer: CompletableDeferred<String?>,
        /** When set, the confirm action stays disabled until the input matches this exact
         *  phrase (case-insensitive, trimmed) — a type-to-confirm guard for dangerous ops. */
        val requireText: String? = null,
        /** Style the confirm button as destructive (red). */
        val destructive: Boolean = false,
        /** Render a multiline TextArea instead of a single-line Input (e.g. a pasted URL
         *  list). Enter inserts a newline rather than submitting. */
        val multiline: Boolean = false,
    ) : ModalRequest(title, message, confirmLabel, cancelLabel)

    /**
     * A small generic form: N declared [params] (TEXT or SELECT), collected into a
     * key→value map. Used by the archive compress dialog (archive name + format,
     * ADV-1214 spec §6). Completes with the values, or null if cancelled.
     */
    class Params(
        title: String,
        message: String?,
        confirmLabel: String,
        cancelLabel: String,
        val params: List<FormField>,
        val completer: CompletableDeferred<Map<String, String>?>,
    ) : ModalRequest(title, message, confirmLabel, cancelLabel)

    /**
     * ADV-1529: where an extract of [file] goes. [besideAt] is the archive's own folder (null at
     * the Shared with me top level), offered first when [besideAllowed]; else My Drive. The
     * person may change it to any folder they may add files to. Completes with the
     * [ExtractChoice], or null if cancelled.
     */
    class ExtractTo(
        title: String,
        confirmLabel: String,
        val file: FileDto,
        val besideAt: ExtractPickerAt?,
        val besideAllowed: Boolean,
        val completer: CompletableDeferred<ExtractChoice?>,
    ) : ModalRequest(title, null, confirmLabel, "Cancel")

    /**
     * Pull-from-URL — collects the URL list and, for any s3:// buckets pasted in,
     * their AWS keys via the adaptive inline/step credential UI (spec §5: 0 buckets
     * unchanged, 1-2 inline, 3+ two-step). Completes with the chosen [PullSubmission],
     * or null if the user cancelled at any point.
     */
    class PullFromUrl(
        val completer: CompletableDeferred<PullSubmission?>,
    ) : ModalRequest(
        title = "Pull from URL",
        message = null,
        confirmLabel = "Pull",
        cancelLabel = "Cancel",
    )

    /**
     * ADV-669 — the two-step OPC run wizard. **Step 1** is a card list of the
     * opinionated use-cases in [catalog], each showing its title, the curator's summary
     * and the underlying nf-core pipeline + revision. **Step 2** is the file/folder
     * picker for the chosen OPC's declared
     * [com.advaita.cloud.drive.ui.store.CuratedInputSpec]s (D4: `format: file-path` = one
     * file, `format: file-set` = N files) — session E's picker, behaviourally unchanged.
     *
     * Per D21 the picker enumerates: a picked folder is walked through the browse API the
     * file table already uses, under the scientist's own session, and the dialog submits
     * an explicit `{file_id, rel}` list — DataHub never expands a folder. Completes with
     * the chosen OPC plus its picks ([OpcRunSelection]), or null if cancelled at any
     * point.
     *
     * `title`/`message` are `val`s fixed at construction, so the per-OPC title and
     * description step 2 shows come from the `displayTitle`/`displayMessage` overrides in
     * [Modal] — exactly how the Pull-from-URL wizard's phase-2 title already works.
     *
     * [source]/[folderFiles] are the folder the modal opened from —
     * [com.advaita.cloud.drive.ui.store.FileBrowserStore.currentPath]/`.files` at open
     * time, or (ADV-1330) the open folder of Shared with me and its rows — used to
     * pre-select obvious candidates (reviewer feedback: don't open on "No files selected"
     * when the folder plainly has the right files); see [CuratedRunUiState]. [source] also
     * says how the picker browses: by path in My Drive, by folder id in a shared folder.
     */
    class CuratedRun(
        val catalog: List<CuratedWorkflowSpec>,
        val source: PickerSource,
        val folderFiles: List<FileDto>,
        val completer: CompletableDeferred<OpcRunSelection?>,
    ) : ModalRequest(
        title = "Run workflow",
        message = null,
        confirmLabel = "Run",
        cancelLabel = "Cancel",
    )
}

/**
 * Local, per-open-modal state for the Pull-from-URL adaptive credential UI. Lives only
 * in this composable's [remember] scope — never touches [FileBrowserStore] — so every
 * typed key vanishes the instant the modal closes (submit or cancel); nothing pull-related
 * is ever folded into app state or persisted client-side (spec §5.3 field hygiene).
 */
private class PullFromUrlUiState {
    var urlsText: String by mutableStateOf("")
    var step: PullModalStep by mutableStateOf(PullModalStep.URLS)
    /** Which bucket's page the credentials step is showing (one bucket per page). */
    var credPage: Int by mutableStateOf(0)
    var useSharedKeys: Boolean by mutableStateOf(false)
    var sharedKeys: BucketKeyInput by mutableStateOf(BucketKeyInput())
    val perBucketKeys = mutableStateMapOf<String, BucketKeyInput>()
}

/**
 * The OPC wizard's Compose holder — one [OpcWizardState] and nothing else, so every
 * transition rule stays in the pure, Node-tested step machine
 * ([com.advaita.cloud.drive.ui.store.choose], [advance], [back]) rather than being
 * re-implemented here. Same discipline as [PullFromUrlUiState]: it lives only in the
 * modal's `remember` scope, so cancelling discards the whole thing.
 */
internal class OpcWizardUiState(catalog: List<CuratedWorkflowSpec>) {
    var state: OpcWizardState by mutableStateOf(initialOpcWizardState(catalog))
    val step: OpcWizardStep get() = state.step
    val chosen: CuratedWorkflowSpec? get() = state.chosen
    val picksByInput: Map<String, List<CuratedPick>> get() = state.picksByInput

    fun choose(opc: CuratedWorkflowSpec) { state = state.choose(opc) }
    fun advance() { state = state.advance() }

    /** "◂ Back" — records step 2's live picks first, so returning to step 1 and coming
     *  straight back preserves them. */
    fun back(picks: Map<String, List<CuratedPick>>) { state = state.withPicks(picks).back() }
}

@Composable
fun Modal(store: FileBrowserStore, pickerPeek: PreviewTooltipStore) {
    val modal = store.activeModal ?: return
    var inputValue by remember(modal) {
        mutableStateOf((modal as? ModalRequest.Prompt)?.defaultValue ?: "")
    }
    // For a type-to-confirm Prompt, the action stays blocked until the phrase matches.
    fun promptMatches(): Boolean {
        val req = (modal as? ModalRequest.Prompt)?.requireText ?: return true
        return inputValue.trim().equals(req, ignoreCase = true)
    }

    // One entry per declared param: a SELECT defaults to its first option (so a submit
    // without touching the control still sends a valid value), a TEXT to its prefill.
    val paramValues = remember(modal) {
        mutableStateMapOf<String, String>().apply {
            (modal as? ModalRequest.Params)?.params?.forEach { p ->
                put(
                    p.key,
                    when (p.type) {
                        FormFieldType.SELECT -> p.options.firstOrNull()?.value ?: ""
                        FormFieldType.TEXT -> p.defaultValue
                    },
                )
            }
        }
    }

    // A required TEXT param (the archive name) must be non-blank before the confirm
    // action unlocks — same spirit as the Prompt's type-to-confirm gate.
    val paramsIncomplete = (modal as? ModalRequest.Params)?.params
        ?.any { it.required && paramValues[it.key].isNullOrBlank() } == true

    // ---- Pull-from-URL adaptive state (spec §5) — only allocated for that modal type. ----
    val pullState = remember(modal) { (modal as? ModalRequest.PullFromUrl)?.let { PullFromUrlUiState() } }
    val pullUrls = pullState?.let { parsePullUrls(it.urlsText) } ?: emptyList()
    val pullBuckets = distinctS3Buckets(pullUrls)
    val pullBucketCounts = bucketObjectCounts(pullUrls)
    // S6 M1: gate the AWS-keys panel on the server's s3 sub-flag. When the connector is OFF but an
    // s3:// URL is pasted, the panel is hidden, a notice shows, and submit is blocked (all-or-nothing,
    // mirroring the backend admission reject). http/https-only pulls are unaffected.
    val s3PullEnabled = store.authConfig?.s3PullEnabled == true
    val pullBlocked = s3PullBlocked(pullBuckets, s3PullEnabled)
    // The wizard only makes sense once the panel is allowed; when blocked we stay on one screen.
    val pullStepFlow = needsStepFlow(pullBuckets) && !pullBlocked
    val pullReady = pullState?.let {
        allBucketsFilled(pullBuckets, it.useSharedKeys, it.sharedKeys, it.perBucketKeys)
    } ?: true
    // Phase 1 = the wizard's URL screen ("Next (N)"); Phase 2 = its paginated credentials
    // screen ("◂ Back" + dynamic title + "Next ▸"/"Pull (N)"). Both are false for the inline
    // (single-bucket) layout, which shows everything on one screen the whole time.
    val pullPhase1 = pullState != null && pullStepFlow && pullState.step == PullModalStep.URLS
    val pullPhase2 = pullState != null && pullStepFlow && pullState.step == PullModalStep.CREDENTIALS
    // One credential page per bucket ("use one key set" collapses to a single page). The page
    // index is *derived* clamped so a composition can never index past a bucket that was just
    // deleted from the URL list; the effect below writes the clamp back into state.
    val pullPageCount = pullState?.let { credentialPageCount(pullBuckets, it.useSharedKeys) } ?: 0
    val pullCredPage = clampCredentialPage(pullState?.credPage ?: 0, pullPageCount)
    val pullLastPage = isLastCredentialPage(pullCredPage, pullPageCount)
    val pullPageFilled = pullState?.let {
        credentialPageFilled(pullBuckets, it.useSharedKeys, it.sharedKeys, it.perBucketKeys, pullCredPage)
    } ?: true
    if (pullState != null) {
        // A bucket removed from the pasted list drops its typed keys from memory too
        // (spec §5.3) — LaunchedEffect so this runs as a side effect, not during composition.
        LaunchedEffect(pullBuckets) {
            val keep = pullBuckets.toSet()
            pullState.perBucketKeys.keys.toList().forEach { if (it !in keep) pullState.perBucketKeys.remove(it) }
        }
        // Persist the clamp: editing the URL list or ticking "use one key set" can remove the
        // page the user is standing on, and a stale index would resurface if pages come back.
        LaunchedEffect(pullBuckets, pullState.useSharedKeys) {
            val clamped = clampCredentialPage(pullState.credPage, credentialPageCount(pullBuckets, pullState.useSharedKeys))
            if (clamped != pullState.credPage) pullState.credPage = clamped
        }
    }

    // ---- OPC run wizard state (ADV-669) — only allocated for that modal type.
    // Lives entirely in this remember scope, same discipline as pullState: nothing picked
    // survives a cancel, and a submit hands the picks to the caller rather than storing them.
    val wizard = remember(modal) { (modal as? ModalRequest.CuratedRun)?.let { OpcWizardUiState(it.catalog) } }
    // Step 2's picker state can't exist until step 1 has chosen an OPC (its declared inputs
    // ARE the picker), so it's keyed on the choice. Re-choosing the same OPC leaves this
    // instance — and therefore its live picks — untouched; choosing a different one rebuilds
    // it, seeded from the wizard (which picksForOpcSwitch has already cleared for a genuine
    // switch, so the seed is empty there and auto-selection runs afresh).
    val curatedState = remember(modal, wizard?.chosen) {
        (modal as? ModalRequest.CuratedRun)?.let { m ->
            wizard?.chosen?.let {
                CuratedRunUiState(it.inputs, m.source, m.folderFiles, wizard.picksByInput, it.knobs)
            }
        }
    }
    val onSelectStep = wizard != null && wizard.step == OpcWizardStep.SELECT
    val curatedReady = if (modal is ModalRequest.CuratedRun && wizard != null) {
        curatedState?.browsingInput == null && canRun(wizard.chosen, curatedState?.picksByInput.orEmpty())
    } else {
        true
    }

    // ---- Extract destination (ADV-1529) — only allocated for that modal type.
    val extractState = remember(modal) { (modal as? ModalRequest.ExtractTo)?.let { ExtractToUiState(it.besideAt, it.besideAllowed) } }
    val extractChoice = extractState?.choice()

    val submit: () -> Unit = {
        when (modal) {
            is ModalRequest.Confirm -> store.resolveModal(true, null)
            is ModalRequest.Prompt -> if (promptMatches()) store.resolveModal(true, inputValue)
            is ModalRequest.Params -> if (!paramsIncomplete) modal.completer.complete(paramValues.toMap())
            is ModalRequest.ExtractTo -> extractChoice?.let { modal.completer.complete(it) }
            is ModalRequest.PullFromUrl -> {
                val st = pullState!!
                if (pullPhase1) {
                    if (pullUrls.isNotEmpty()) { st.step = PullModalStep.CREDENTIALS; st.credPage = 0 }
                } else if (pullPhase2 && !pullLastPage) {
                    // Mid-wizard: advance one bucket rather than submitting.
                    if (pullPageFilled) st.credPage = pullCredPage + 1
                } else if (pullUrls.isNotEmpty() && pullReady) {
                    val creds = buildS3Credentials(pullBuckets, st.useSharedKeys, st.sharedKeys, st.perBucketKeys)
                    modal.completer.complete(PullSubmission(pullUrls, creds))
                }
            }
            is ModalRequest.CuratedRun -> {
                val wiz = wizard!!
                if (onSelectStep) {
                    // Mid-wizard: Next advances to the picker rather than submitting.
                    if (canAdvance(wiz.chosen)) wiz.advance()
                } else if (curatedReady) {
                    val opc = wiz.chosen
                    val picks = curatedState?.picksByInput?.toMap()
                    if (opc != null && picks != null) {
                        modal.completer.complete(
                            OpcRunSelection(opc, picks, curatedState.knobValues.toMap()),
                        )
                    }
                }
            }
        }
    }
    val cancel: () -> Unit = {
        when (modal) {
            is ModalRequest.PullFromUrl -> modal.completer.complete(null)
            is ModalRequest.CuratedRun -> modal.completer.complete(null)
            is ModalRequest.Params -> modal.completer.complete(null)
            is ModalRequest.ExtractTo -> modal.completer.complete(null)
            else -> store.resolveModal(false, null)
        }
    }
    // "◂ Back" walks the pull wizard backwards a bucket at a time, then out to the URL
    // screen; for the OPC wizard it returns from the picker to the card list, carrying the
    // picks so far so a round trip doesn't lose them.
    val back: () -> Unit = {
        val st = pullState
        if (st != null) {
            if (pullCredPage > 0) st.credPage = pullCredPage - 1 else st.step = PullModalStep.URLS
        } else if (wizard != null) {
            wizard.back(curatedState?.picksByInput?.toMap().orEmpty())
        }
    }

    // For a multiline (URL-list) prompt: a live count of the non-blank, de-duped
    // lines — drives the "Pull (N)" button label and disables it when empty.
    val urlCount: Int? = (modal as? ModalRequest.Prompt)?.takeIf { it.multiline }?.let {
        inputValue.split('\n', '\r').map { s -> s.trim() }.filter { it.isNotEmpty() }.distinct().size
    }
    val isWide = urlCount != null || modal is ModalRequest.PullFromUrl || modal is ModalRequest.CuratedRun

    // On a credentials page the H2 is the ONLY title (the in-panel header was dropped after S5
    // feedback — with one bucket per page a second "N private buckets" heading was redundant and
    // cost vertical room), and it carries the page position.
    // Keeps the 🔒 that the dropped in-panel header used to carry — it's the trust cue for a
    // screen asking for someone's AWS keys, so it belongs on whichever title survives.
    val displayTitle = when {
        pullPhase2 -> {
            val counter = credentialPageCounter(pullCredPage, pullPageCount)
            if (counter == null) {
                "🔒 AWS keys required"
            } else {
                "🔒 AWS keys required · ${counter.replaceFirstChar { it.lowercaseChar() }}"
            }
        }
        // Step 2 is about ONE OPC, so it takes that OPC's title; step 1 keeps the generic
        // "Run workflow" because the scientist hasn't chosen yet.
        wizard != null && !onSelectStep -> wizard.chosen?.title ?: modal.title
        else -> modal.title
    }
    val displayMessage = when {
        onSelectStep -> "Choose a workflow to run against your files."
        wizard != null -> wizard.chosen?.description
        else -> modal.message
    }
    val mainLabel = when {
        pullPhase1 -> "Next (${pullUrls.size})"
        pullPhase2 && !pullLastPage -> "Next ▸"
        modal is ModalRequest.PullFromUrl -> "${modal.confirmLabel} (${pullUrls.size})"
        onSelectStep -> "Next ▸"
        urlCount != null && urlCount > 0 -> "${modal.confirmLabel} ($urlCount)"
        // ADV-1529: the button names the place, so nobody has to read back up to know where.
        modal is ModalRequest.ExtractTo && extractState != null && extractChoice != null ->
            "${modal.confirmLabel} to ${extractState.at.label()}"
        else -> modal.confirmLabel
    }
    val mainDisabled = when {
        pullPhase1 -> pullUrls.isEmpty()
        // Mid-wizard "Next" is gated on THIS page's bucket only, so a user can't skip a bucket.
        pullPhase2 && !pullLastPage -> !pullPageFilled
        // pullBlocked: an s3:// URL with the connector off blocks the whole submit (all-or-nothing).
        modal is ModalRequest.PullFromUrl -> pullUrls.isEmpty() || !pullReady || pullBlocked
        // Step 1's Next needs a chosen OPC and nothing else.
        onSelectStep -> !canAdvance(wizard?.chosen)
        // Step 2's Run is disabled while browsing (an in-progress pick isn't yet part of
        // picksByInput) and until every declared input has a usable pick set.
        modal is ModalRequest.CuratedRun -> !curatedReady
        modal is ModalRequest.Params -> paramsIncomplete
        modal is ModalRequest.ExtractTo -> extractChoice == null
        else -> !promptMatches() || urlCount == 0
    }

    // Deliberately NO onClick on the backdrop: dismissing on an outside click is too easy to
    // trigger by accident and silently discards whatever the user typed (a half-filled
    // Pull-from-URL wizard loses its URL list and credentials). Cancelling is explicit — the
    // Cancel button.
    Div(attrs = {
        classes("modal-backdrop")
    }) {
        Div(attrs = {
            // Wide variant for content that needs room: the multiline URL list, or the
            // Pull-from-URL modal (URL list + potentially a credential panel).
            if (isWide) classes("modal", "wide") else classes("modal")
        }) {
            H2(attrs = { classes("modal-title") }) { Text(displayTitle) }
            if (displayMessage != null) {
                P(attrs = { classes("modal-message") }) { Text(displayMessage) }
            }
            if (modal is ModalRequest.Prompt) {
                if (modal.inputLabel.isNotBlank()) {
                    Span(attrs = { classes("modal-label") }) { Text(modal.inputLabel) }
                }
                if (modal.multiline) {
                    // url-list: monospace + no soft-wrap so each line stays one URL
                    // (line count == URL count); horizontal scroll for long tails.
                    TextArea(value = inputValue, attrs = {
                        classes("modal-input", "url-list")
                        attr("rows", "10")
                        attr("wrap", "off")
                        attr("spellcheck", "false")
                        placeholder(modal.placeholder)
                        // Focus explicitly (autoFocus doesn't fire for a dynamically-mounted modal).
                        ref { element -> element.focus(); onDispose { } }
                        onInput { inputValue = it.value }
                        // NOT Enter — newlines are content in a multiline field.
                        onKeyDown { e -> if (e.key == "Escape") cancel() }
                    })
                } else {
                    Input(InputType.Text, attrs = {
                        classes("modal-input")
                        value(inputValue)
                        placeholder(modal.placeholder)
                        autoFocus()
                        // autoFocus() (the HTML attribute) doesn't fire for a dynamically-mounted
                        // modal, so focus the field explicitly once it's in the DOM.
                        ref { element -> element.focus(); onDispose { } }
                        onInput { inputValue = it.value }
                        onKeyDown { e ->
                            when (e.key) {
                                "Enter" -> submit()
                                "Escape" -> cancel()
                            }
                        }
                    })
                }
            }
            if (modal is ModalRequest.Params) {
                modal.params.forEach { p ->
                    Span(attrs = { classes("modal-label") }) { Text(p.label) }
                    when (p.type) {
                        FormFieldType.TEXT -> Input(InputType.Text, attrs = {
                            classes("modal-input")
                            value(paramValues[p.key] ?: "")
                            autoFocus()
                            // autoFocus() doesn't fire for a dynamically-mounted modal.
                            ref { element -> element.focus(); onDispose { } }
                            onInput { paramValues[p.key] = it.value }
                            onKeyDown { e ->
                                when (e.key) {
                                    "Enter" -> if (!paramsIncomplete) submit()
                                    "Escape" -> cancel()
                                }
                            }
                        })
                        FormFieldType.SELECT -> Select(attrs = {
                            classes("modal-input")
                            onChange { ev -> ev.value?.let { paramValues[p.key] = it } }
                        }) {
                            p.options.forEach { opt ->
                                Option(opt.value, attrs = {
                                    if (paramValues[p.key] == opt.value) selected()
                                }) { Text(opt.label) }
                            }
                        }
                    }
                }
            }
            if (modal is ModalRequest.ExtractTo && extractState != null) ExtractToBody(extractState, modal)
            if (pullState != null) {
                PullFromUrlBody(
                    state = pullState,
                    urls = pullUrls,
                    buckets = pullBuckets,
                    bucketCounts = pullBucketCounts,
                    stepFlow = pullStepFlow,
                    s3Enabled = s3PullEnabled,
                    page = pullCredPage,
                    pageCount = pullPageCount,
                    onCancelKeystroke = cancel,
                )
            }
            if (modal is ModalRequest.CuratedRun && wizard != null) {
                if (onSelectStep) {
                    OpcSelectView(wizard = wizard, catalog = modal.catalog)
                } else {
                    val chosen = wizard.chosen
                    if (curatedState != null && chosen != null) {
                        CuratedRunBody(
                            state = curatedState,
                            specs = chosen.inputs,
                            source = modal.source,
                            knobs = chosen.knobs,
                            peek = pickerPeek,
                        )
                    }
                }
            }
            // While the curated picker is browsing Drive for one input, it renders its OWN
            // action row (◂ Back to inputs / Add checked / Add N files) — the outer
            // Cancel/Run row belongs to the overview only, so it's suppressed here to avoid
            // two stacked action rows. Step 1 has no such inner row, so it is never suppressed.
            val suppressOuterActions = !onSelectStep && curatedState != null && curatedState.browsingInput != null
            if (!suppressOuterActions) Div(attrs = { classes("modal-actions") }) {
                if (pullPhase2 || (wizard != null && !onSelectStep)) {
                    Button(attrs = {
                        classes("btn")
                        onClick { back() }
                    }) { Text("◂ Back") }
                }
                Button(attrs = {
                    classes("btn")
                    onClick { cancel() }
                }) { Text(modal.cancelLabel) }
                Button(attrs = {
                    val danger = (modal as? ModalRequest.Confirm)?.destructive == true ||
                        (modal as? ModalRequest.Prompt)?.destructive == true
                    if (danger) classes("btn", "danger") else classes("btn", "primary")
                    if (mainDisabled) attr("disabled", "true")
                    onClick { submit() }
                }) {
                    Text(mainLabel)
                }
            }
        }
    }
}

/**
 * The URL textarea + (if any s3:// buckets are detected) the credential panel — spec §5.1/§5.2
 * as revised by S5 vetting. Both the inline (single-bucket) and wizard (2+ buckets) layouts
 * share this body; [stepFlow] + [state].step decide what's visible, and in the wizard [page]
 * selects which single bucket's block is shown.
 */
@Composable
private fun PullFromUrlBody(
    state: PullFromUrlUiState,
    urls: List<String>,
    buckets: List<String>,
    bucketCounts: Map<String, Int>,
    stepFlow: Boolean,
    s3Enabled: Boolean,
    page: Int,
    pageCount: Int,
    onCancelKeystroke: () -> Unit,
) {
    val showUrls = !stepFlow || state.step == PullModalStep.URLS
    // Never render the AWS-keys panel when the s3 connector is off (S6 M1) — a notice shows instead.
    val showCreds = s3Enabled && buckets.isNotEmpty() && (!stepFlow || state.step == PullModalStep.CREDENTIALS)
    val showS3Disabled = buckets.isNotEmpty() && !s3Enabled

    if (showUrls) {
        P(attrs = { classes("modal-message") }) {
            Text("Paste one or more file URLs (http / https / s3), one per line. They'll download into this folder.")
        }
        Span(attrs = { classes("modal-label") }) { Text("URLs") }
        TextArea(value = state.urlsText, attrs = {
            classes("modal-input", "url-list")
            attr("rows", "10")
            attr("wrap", "off")
            attr("spellcheck", "false")
            placeholder("https://example.com/data/file1.tar\ns3://your-bucket/path/to/file.fastq.gz")
            ref { element -> element.focus(); onDispose { } }
            onInput { state.urlsText = it.value }
            onKeyDown { e -> if (e.key == "Escape") onCancelKeystroke() }
        })
    }

    if (showS3Disabled) {
        // s3:// pasted but the connector is off server-side — no keys panel, submit blocked.
        Div(attrs = { classes("s3-cred-panel", "s3-disabled") }) {
            P(attrs = { classes("s3-cred-copy") }) {
                Text(
                    "S3 pull is not enabled on this server. Remove the s3:// URL(s) to continue, " +
                        "or contact your administrator.",
                )
            }
        }
    }

    if (showCreds) {
        Div(attrs = {
            if (stepFlow) classes("s3-cred-panel", "scrollable") else classes("s3-cred-panel")
        }) {
            // No in-panel header in the wizard: the modal H2 is the single title (S5 feedback).
            // The inline single-bucket layout keeps one, since its H2 still says "Pull from URL".
            if (!stepFlow) {
                Div(attrs = { classes("s3-cred-header") }) { Text("🔒 AWS keys required") }
            }
            P(attrs = { classes("s3-cred-copy") }) {
                Text(
                    "Your keys are encrypted the instant you submit, used only to download, and " +
                        "permanently deleted the moment each download finishes or fails. They're never " +
                        "stored in plaintext, never written to logs, and never shown again.",
                )
            }
            P(attrs = { classes("s3-cred-copy") }) {
                Text(
                    "Please supply keys with READ-ONLY access to just these buckets. Even safer: " +
                        "generate temporary credentials yourself and paste the session token — AWS will " +
                        "auto-expire them.",
                )
            }
            // The "one key set" shortcut lives on the FIRST page only — ticking it collapses the
            // whole wizard to that single page, so page 0 is the only place it can be reached.
            if (buckets.size >= 2 && page == 0) {
                Label(attrs = { classes("modal-check") }) {
                    CheckboxInput(checked = state.useSharedKeys, attrs = {
                        onChange { ev -> state.useSharedKeys = ev.value }
                    })
                    Span { Text("Use one key set for all buckets") }
                }
            }
            if (state.useSharedKeys && buckets.size >= 2) {
                BucketCredentialBlock(
                    label = "All buckets",
                    value = state.sharedKeys,
                    // Apply against the LIVE state, not the `value` this composable was invoked
                    // with — see the onChange kdoc below for why that distinction matters.
                    onChange = { transform -> state.sharedKeys = transform(state.sharedKeys) },
                )
            } else {
                // Exactly ONE bucket per page (S5 feedback) — stacking every block on one screen
                // overflowed the modal at just two buckets.
                val bucket = credentialPageBucket(buckets, useSharedKeys = false, page = page)
                if (bucket != null) {
                    val count = bucketCounts[bucket] ?: 0
                    val label = if (stepFlow) {
                        "s3://$bucket  ·  $count object${if (count == 1) "" else "s"}"
                    } else {
                        "s3://$bucket"
                    }
                    BucketCredentialBlock(
                        label = label,
                        value = state.perBucketKeys[bucket] ?: BucketKeyInput(),
                        onChange = { transform ->
                            state.perBucketKeys[bucket] = transform(state.perBucketKeys[bucket] ?: BucketKeyInput())
                        },
                    )
                }
            }
            // Progress: one dot per bucket (filled once its keys are in) + "Bucket N of M", so
            // it's obvious there's more to do on the next page. Absent when there's one page.
            val dots = credentialDots(
                buckets, state.useSharedKeys, state.sharedKeys, state.perBucketKeys, page,
            )
            if (dots.isNotEmpty()) {
                Div(attrs = { classes("s3-cred-progress") }) {
                    Div(attrs = { classes("s3-cred-dots") }) {
                        dots.forEach { dot ->
                            Span(attrs = {
                                classes("s3-cred-dot")
                                if (dot.filled) classes("filled")
                                if (dot.current) classes("current")
                            }) {}
                        }
                    }
                    credentialPageCounter(page, pageCount)?.let {
                        Span(attrs = { classes("s3-cred-counter") }) { Text(it) }
                    }
                }
            }
        }
    }
}

/**
 * One bucket's (or the shared) Access key ID / Secret access key / optional Session
 * token fields. Secret + session token are password-typed with autocomplete/spellcheck
 * off (spec §5.3 field hygiene) — no autofill, no password-manager capture.
 *
 * [onChange] takes a *transform* of the current value, not a replacement value — it must
 * be applied by the caller against the LIVE state (e.g. `state.perBucketKeys[bucket]`),
 * not against this composable's `value` parameter. Two sibling fields (Access key ID,
 * Secret access key) can each fire `onInput` in the same tick, both closing over the same
 * `value` this composable was invoked with; if a caller applied `value.copy(...)` directly,
 * the second field's update would silently clobber the first's the moment they land before
 * a recomposition — a real lost-update race reproduced here via a fast scripted paste
 * (Playwright's `.fill()` on both fields back-to-back). Passing a transform instead lets
 * the caller fold each edit onto whatever is currently in state, so both stick regardless
 * of recomposition timing.
 */
@Composable
private fun BucketCredentialBlock(
    label: String,
    value: BucketKeyInput,
    onChange: ((BucketKeyInput) -> BucketKeyInput) -> Unit,
) {
    Div(attrs = { classes("bucket-block") }) {
        Div(attrs = { classes("bucket-block-label") }) { Text(label) }
        Span(attrs = { classes("modal-label") }) { Text("Access key ID") }
        Input(InputType.Text, attrs = {
            classes("modal-input")
            value(value.accessKeyId)
            attr("autocomplete", "off")
            attr("spellcheck", "false")
            onInput { ev -> onChange { current -> current.copy(accessKeyId = ev.value) } }
        })
        Span(attrs = { classes("modal-label") }) { Text("Secret access key") }
        PasswordInput(value = value.secretAccessKey, attrs = {
            classes("modal-input")
            attr("autocomplete", "off")
            attr("spellcheck", "false")
            onInput { ev -> onChange { current -> current.copy(secretAccessKey = ev.value) } }
        })
        Div(attrs = { classes("bucket-block-token") }) {
            Button(attrs = {
                classes("btn", "link")
                onClick { onChange { current -> current.copy(sessionTokenExpanded = !current.sessionTokenExpanded) } }
            }) {
                val arrow = if (value.sessionTokenExpanded) "▾" else "▸"
                Text("$arrow Session token (optional, for temporary credentials)")
            }
            if (value.sessionTokenExpanded) {
                PasswordInput(value = value.sessionToken, attrs = {
                    classes("modal-input")
                    attr("autocomplete", "off")
                    attr("spellcheck", "false")
                    onInput { ev -> onChange { current -> current.copy(sessionToken = ev.value) } }
                })
            }
        }
    }
}
